import { useState } from 'react';
import { SimulatedBadge } from '@pointfive/ui';
import { api } from '@/client/api';
import { useProcedure } from '@/client/useProcedure';
import { ActionButton, ago, Section, SettingCard, SettingRow, ToneChip } from './kit';

/** Settings → Security.
 *
 *  Three questions a trader actually has: who is signed in as me, how do I stop them, and can I check that the record
 *  of what happened is intact. Everything on the screen answers one of those from real server state.
 *
 *  What is deliberately **absent** is as important. There is no password field and no "change password" button,
 *  because no password lives in this system — the identity provider owns the credential. A settings screen offering
 *  to change a password it does not hold would be theatre, and the trader would find out at the worst moment.
 */
export function SecuritySection() {
  const now = Date.now();
  const sessions = useProcedure(() => api.me.sessions.list(), []);
  const security = useProcedure(() => api.me.security.get(), []);
  const [busy, setBusy] = useState<string | null>(null);

  const revoke = async (sessionId: string): Promise<void> => {
    setBusy(sessionId);
    try {
      await api.me.sessions.revoke({ sessionId });
      sessions.reload();
    } finally {
      setBusy(null);
    }
  };

  const revokeOthers = async (): Promise<void> => {
    setBusy('others');
    try {
      await api.me.sessions.revokeOthers({
        idempotencyKey: `revoke-others-${Date.now().toString(36)}`,
      });
      sessions.reload();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <Section state={security.state} reload={security.reload}>
        {(s) => (
          <SettingCard
            title="Sign-in"
            sub="Your credential is held by the identity provider, not by PointFive. There is no password here to change."
            extra={s.authBinding === 'dev' ? <SimulatedBadge what="sandbox sign-in" /> : null}
          >
            <SettingRow label="Email" hint="The address the provider authenticates.">
              <span style={{ fontSize: '11.5px', color: 'var(--text-mid)' }}>{s.email}</span>
            </SettingRow>
            <SettingRow
              label="Two-factor authentication"
              hint={
                s.twoFactor === 'provider-managed'
                  ? 'Managed by your identity provider — change it there and it applies here immediately.'
                  : 'The sandbox sign-in has no second factor. Not for production use.'
              }
            >
              <ToneChip tone={s.twoFactor === 'provider-managed' ? 'pos' : 'neg'}>
                {s.twoFactor === 'provider-managed' ? 'Provider-managed' : 'Off'}
              </ToneChip>
            </SettingRow>
            <SettingRow
              label="Audit trail"
              hint={
                s.audit.verified
                  ? `${s.audit.events.toLocaleString('en-US')} recorded ${s.audit.events === 1 ? 'event' : 'events'}, chain verified. A log you cannot check is a log you are asked to trust.`
                  : `The hash chain fails to reproduce at sequence ${s.audit.brokenAt ?? '—'}. Someone should look at this.`
              }
            >
              <ToneChip tone={s.audit.verified ? 'pos' : 'neg'}>
                {s.audit.verified ? 'Verified' : 'Broken'}
              </ToneChip>
            </SettingRow>
          </SettingCard>
        )}
      </Section>

      <Section state={sessions.state} reload={sessions.reload}>
        {(data) => {
          const others = data.sessions.filter((s) => !s.current).length;
          return (
            <>
              <SettingCard
                title="Active sessions"
                sub="Every browser and device currently signed in as you."
                extra={
                  others > 0 ? (
                    <ActionButton
                      onClick={() => void revokeOthers()}
                      title="Sign out every session but this one"
                      disabled={busy !== null}
                    >
                      {busy === 'others'
                        ? 'Signing out…'
                        : `Sign out ${others} other${others === 1 ? '' : 's'}`}
                    </ActionButton>
                  ) : null
                }
              >
                {data.sessions.map((s) => (
                  <SettingRow
                    key={s.sessionId}
                    label={
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        {s.deviceLabel}
                        {s.current ? <ToneChip tone="pos">This device</ToneChip> : null}
                      </span>
                    }
                    hint={
                      <span title={`Signed in ${s.issuedAt} · expires ${s.expiresAt}`}>
                        {s.platform}
                        {s.approxLocation === null ? '' : ` · ${s.approxLocation}`} · last seen{' '}
                        {ago(s.lastSeenAt, now)}
                      </span>
                    }
                  >
                    {/* No revoke on the current session: that is sign-out, which has a different consequence and
                        already exists in the account menu. */}
                    {s.current ? null : (
                      <ActionButton
                        onClick={() => void revoke(s.sessionId)}
                        title={`Revoke the session on ${s.deviceLabel}`}
                        disabled={busy !== null}
                        danger
                      >
                        {busy === s.sessionId ? 'Revoking…' : 'Revoke'}
                      </ActionButton>
                    )}
                  </SettingRow>
                ))}
              </SettingCard>

              {data.dormantDevices.length === 0 ? null : (
                <SettingCard
                  title="Devices seen before"
                  sub="Signed in from these in the past, with no live session now. Listed so a device you do not recognise stays visible after its session lapses."
                >
                  {data.dormantDevices.map((d) => (
                    <SettingRow
                      key={d.deviceId}
                      label={d.deviceLabel}
                      hint={`${d.platform} · last seen ${ago(d.lastSeenAt, now)}`}
                    />
                  ))}
                </SettingCard>
              )}
            </>
          );
        }}
      </Section>
    </div>
  );
}
